import { BellOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { Badge, Button, Popover, Timeline } from "antd";
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { parseISO } from "date-fns";
import api from "../../services/api";
import { useToast } from "../../hooks/toast";
import { IUser } from "../Users";
import "./styles.css";
import UserAvatar from "../../components/UserAvatar";

interface INotification {
  id: string;
  notification_type: string;
  adicional_data?: string;
  read: boolean;
  sender?: IUser;
  created_at: string;
}

const Notifications: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const { addToast } = useToast();
  const { t } = useTranslation("notifications");

  useEffect(() => {
    api
      .get<INotification[]>("/notifications")
      .then(response => {
        setNotifications(response.data);
      })
      .catch(err => {
        if (axios.isAxiosError(err)) {
          addToast({
            type: "error",
            title: t("error-finding-notifications"),
            description: t("error-finding-notifications-message"),
          });
        }
      })
      .finally(() => setLoading(false));
    const interval = setInterval(() => {
      setLoading(true);
      api
        .get<INotification[]>("/notifications")
        .then(response => {
          setNotifications(response.data);
        })
        .catch(err => {
          if (axios.isAxiosError(err)) {
            addToast({
              type: "error",
              title: t("error-finding-notifications"),
              description: t("error-finding-notifications-message"),
            });
          }
        })
        .finally(() => setLoading(false));
    }, 60000);
    return () => clearInterval(interval);
  }, [addToast]);

  // const notifications = useMemo(
  //   () => [
  //     {
  //       message: "Apontamento de horas não lançado nos últimos x dias",
  //       read: false,
  //       date: "29/07/2022",
  //     },
  //     {
  //       message: "A atividade XPTO foi atribuida a você.",
  //       read: true,
  //       date: "29/07/2022",
  //     },
  //   ],
  //   [],
  // );

  const handleReadNotification = useCallback(
    (id: string) => {
      api
        .patch<INotification>(`/notifications/${id}/read`)
        .then(() => {
          setNotifications(old => [
            ...old.map(notif => {
              if (notif.id === id) {
                // eslint-disable-next-line no-param-reassign
                notif.read = true;
              }
              return notif;
            }),
          ]);
        })
        .catch(err => {
          if (axios.isAxiosError(err)) {
            addToast({
              type: "error",
              title: t("error-reading"),
              description: t("error-reading-message"),
            });
          }
        })
        .finally(() => setLoading(false));
    },
    [addToast],
  );

  const handleReadAllNotification = useCallback(() => {
    api
      .patch<void>(`/notifications/read-all`)
      .then(() => {
        setNotifications(old => [
          ...old.map(notif => {
            // eslint-disable-next-line no-param-reassign
            notif.read = true;
            return notif;
          }),
        ]);
      })
      .catch(err => {
        if (axios.isAxiosError(err)) {
          addToast({
            type: "error",
            title: t("error-reading-all"),
            description: t("error-reading-all-message"),
          });
        }
      })
      .finally(() => setLoading(false));
  }, [addToast]);

  const numberToRead = useMemo(
    () => notifications.filter(n => !n.read).length,
    [notifications],
  );

  const content = useCallback(
    () => (
      <>
        {numberToRead > 0 && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={() => handleReadAllNotification()}
              style={{ marginRight: 10 }}
              type="link"
            >
              {t("mark-all-as-read")}
            </Button>
          </div>
        )}
        <div style={{ maxHeight: 450, overflow: "scroll" }}>
          <Timeline style={{ padding: 18, paddingLeft: 24, paddingBottom: 0 }}>
            {notifications.map(notif => (
              <Timeline.Item
                key={notif.id}
                color={notif.read ? "orange" : "red"}
                className={notif.read ? "read" : "unread"}
                dot={
                  notif.sender ? (
                    <UserAvatar
                      name={notif.sender.name}
                      url={notif.sender.avatar_url}
                      size={32}
                    />
                  ) : (
                    <ClockCircleOutlined className="timeline-clock-icon" />
                  )
                }
              >
                <Link
                  style={{
                    color: notif.read
                      ? "var(--dark-grey-70"
                      : "var(--dark-grey)",
                  }}
                  to="/app/activities"
                >
                  {t(notif.notification_type, {
                    sender: notif.sender?.name,
                    data: notif.adicional_data,
                  })}
                </Link>

                <div
                  style={{
                    fontSize: ".8rem",
                    color: "rgba(0,0,0,0.2)",
                    margin: "0",
                  }}
                >
                  {`${parseISO(notif.created_at).toLocaleDateString(
                    "pt-BR",
                  )} às ${parseISO(notif.created_at).toLocaleTimeString(
                    "pt-BR",
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                    },
                  )}`}
                  {/* <Button type="link">Minhas atividades</Button> */}
                </div>
                <div>
                  {!notif.read && (
                    <Button
                      style={{ padding: 0 }}
                      onClick={() => handleReadNotification(notif.id)}
                      type="link"
                    >
                      {t("mark-as-read")}
                    </Button>
                  )}
                </div>
              </Timeline.Item>
            ))}
          </Timeline>
        </div>
      </>
    ),
    [handleReadNotification, notifications, numberToRead, t],
  );

  return (
    <Popover
      placement="bottomRight"
      // title="Notificações"
      arrowPointAtCenter
      content={content}
      trigger="click"
    >
      <Badge count={numberToRead}>
        <Button type="default" shape="circle" icon={<BellOutlined />} />
      </Badge>
    </Popover>
  );
};

export default Notifications;
